import React from "react";
import {
  Avatar,
  Box,
  Button,
  Divider,
  Flex,
  HStack,
  Image,
  Link,
  Spacer,
  Text,
  VStack,
} from "@chakra-ui/react";
import { NavLink, Outlet, useNavigate } from "react-router-dom";
import { VMS_BRAND, VMS_COLORS } from "../constants/theme";
import { clearVmsSession, getVmsUser, isVmsGeneralAdmin } from "../utils/auth";
import {
  FiActivity,
  FiClipboard,
  FiFileText,
  FiGrid,
  FiList,
  FiLink,
  FiLogOut,
  FiMapPin,
  FiSettings,
  FiShield,
  FiUsers,
} from "react-icons/fi";

const navSections = (isAdmin) => [
  {
    title: "OPERATIONS",
    items: [
      { to: "/vms/dashboard", label: "Dashboard", icon: FiGrid },
      { to: "/vms/requests", label: "Visitor Requests", icon: FiClipboard },
      { to: "/vms/booking-links", label: "Booking Links", icon: FiLink },
      ...(isAdmin
        ? [
            { to: "/vms/offices", label: "Offices", icon: FiMapPin },
            { to: "/vms/gates", label: "Gates", icon: FiMapPin },
          ]
        : []),
    ],
  },
  ...(isAdmin
    ? [
        {
          title: "USER MANAGEMENT",
          items: [
            { to: "/vms/security-users", label: "Security Personnel", icon: FiShield },
            { to: "/vms/people-users", label: "Office Personnel", icon: FiUsers },
          ],
        },
      ]
    : []),
  {
    title: "INSIGHTS",
    items: [
      { to: "/vms/reports/visits", label: "Analytics & Reports", icon: FiFileText },
      ...(isAdmin ? [{ to: "/vms/activity-log", label: "Activity Feed", icon: FiActivity }] : []),
    ],
  },
  {
    title: "ACCOUNT",
    items: [{ to: "/vms/change-password", label: "Settings", icon: FiSettings }],
  },
];

const VmsShell = () => {
  const navigate = useNavigate();
  const user = getVmsUser();
  const isAdmin = isVmsGeneralAdmin();
  const userOfficeName = user?.office?.name || user?.office_name || user?.officeName || "";

  const handleLogout = () => {
    clearVmsSession();
    navigate("/vms/login");
  };

  return (
    <Flex minH="100vh" bg={VMS_COLORS.bg} p={{ base: 3, md: 4 }} gap={4}>
      <Box
        w={{ base: "88px", md: "250px" }}
        bg={VMS_COLORS.primary}
        color="white"
        borderRadius="16px"
        p={4}
        boxShadow="0 18px 36px rgba(11,122,32,0.25)"
      >
        <VStack align="stretch" spacing={4} h="full">
          <HStack spacing={0} justify="center">
            <Box
              w="96px"
              h="96px"
              borderRadius="full"
              overflow="hidden"
              display="flex"
              alignItems="center"
              justifyContent="center"
            >
              <Image
                src="/Katsinalogo.png"
                alt="Katsina State Logo"
                w="100%"
                h="100%"
                borderRadius="full"
                objectFit="cover"
              />
            </Box>
          </HStack>

          <VStack align="stretch" spacing={4} mt={3}>
            {navSections(isAdmin).map((section) => (
              <Box key={section.title}>
                <Text
                  display={{ base: "none", md: "block" }}
                  px={3}
                  mb={1}
                  fontSize="11px"
                  letterSpacing="0.08em"
                  fontWeight="700"
                  color="whiteAlpha.800"
                >
                  {section.title}
                </Text>
                <VStack align="stretch" spacing={2}>
                  {section.items.map((item) => (
                    <Link
                      key={item.to}
                      as={NavLink}
                      to={item.to}
                      px={{ base: 2, md: 4 }}
                      py={3}
                      borderRadius="999px"
                      _hover={{ textDecoration: "none", bg: "whiteAlpha.200" }}
                      _activeLink={{
                        bg: "white",
                        color: VMS_COLORS.primary,
                        fontWeight: "700",
                        boxShadow: "0 10px 24px rgba(255,255,255,0.25)",
                      }}
                      transition="all 0.18s ease"
                    >
                      <HStack justify={{ base: "center", md: "flex-start" }}>
                        <Box as={item.icon} fontSize="20px" />
                        <Text display={{ base: "none", md: "block" }} fontSize="md">
                          {item.label}
                        </Text>
                      </HStack>
                    </Link>
                  ))}
                </VStack>
              </Box>
            ))}
          </VStack>

          <Spacer />
          <Divider borderColor="whiteAlpha.400" />
          <Button
            leftIcon={<FiLogOut />}
            size="sm"
            variant="ghost"
            color="white"
            _hover={{ bg: "whiteAlpha.200" }}
            onClick={handleLogout}
            justifyContent={{ base: "center", md: "flex-start" }}
          >
            <Text display={{ base: "none", md: "block" }}>Sign out</Text>
          </Button>
        </VStack>
      </Box>

      <Box flex="1" bg="white" borderRadius="16px" border="1px solid" borderColor="gray.200" p={4}>
        <HStack
          mb={5}
          px={3}
          py={3}
          bg="gray.50"
          borderRadius="12px"
          border="1px solid"
          borderColor="gray.100"
        >
          <Spacer />
          <HStack spacing={2}>
            <Avatar size="xs" name={user?.name || user?.email || "VMS"} />
            <Box>
              <Text fontSize="xs" color="gray.600" lineHeight="1.2">
                {user?.name || user?.email || "VMS User"}
              </Text>
              {userOfficeName ? (
                <Text fontSize="11px" color="gray.500" lineHeight="1.2">
                  {userOfficeName}
                </Text>
              ) : null}
            </Box>
          </HStack>
        </HStack>
        <Outlet />
      </Box>
    </Flex>
  );
};

export default VmsShell;
